import React from 'react';
import { Box, keyframes,Typography } from '@mui/material';

export interface BrandLoaderProps {
  /** Color variant. Light for light backgrounds, dark for dark/navy backgrounds. @default 'light' */
  variant?: 'light' | 'dark';
  /** Size preset. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** Optional text label rendered beside the indicator. */
  label?: string;
  /** Extra class name on the root element. */
  className?: string;
}

const SIZE_MAP = {
  sm: { square: 10, radius: 2.5, gap: 4, fontSize: 12 },
  md: { square: 14, radius: 3, gap: 5, fontSize: 13 },
  lg: { square: 18, radius: 3.5, gap: 6, fontSize: 14 },
} as const;

const COLORS = {
  light: { primary: '#1C1E85', secondary: '#FD96C9' },
  dark: { primary: '#FD96C9', secondary: '#FFF2F5' },
} as const;

const gentleWave = keyframes`
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  35% {
    transform: rotate(45deg) scale(0.82);
  }
  65% {
    transform: rotate(45deg) scale(0.82);
  }
  85% {
    transform: rotate(0deg) scale(1.06);
  }
`;

/**
 * JotPsych branded loading indicator.
 *
 * Three colored squares rotate sequentially to 45° (diamond) and back,
 * creating a gentle ripple effect.
 */
export const BrandLoader: React.FC<BrandLoaderProps> = ({
  variant = 'light',
  size = 'md',
  label,
  className,
}) => {
  const dims = SIZE_MAP[size];
  const colors = COLORS[variant];

  const squareBase = {
    width: dims.square,
    height: dims.square,
    borderRadius: `${dims.radius}px`,
    animation: `${gentleWave} 2s ease-in-out infinite`,
    flexShrink: 0,
  } as const;

  return (
    <Box
      className={className}
      role="status"
      aria-label={label || 'Loading'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: label ? `${dims.gap + 4}px` : 0,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: `${dims.gap}px`,
        }}
      >
        <Box sx={{ ...squareBase, bgcolor: colors.secondary, animationDelay: '0s' }} />
        <Box sx={{ ...squareBase, bgcolor: colors.primary, animationDelay: '0.2s' }} />
        <Box sx={{ ...squareBase, bgcolor: colors.secondary, animationDelay: '0.4s' }} />
      </Box>
      {label && (
        <Typography
          component="span"
          sx={{
            fontSize: dims.fontSize,
            fontWeight: 500,
            color: variant === 'dark' ? 'rgba(255,255,255,0.6)' : 'textColors.lightText',
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};
